import Box from "@mui/material/Box";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSnackbar } from "notistack";
import AlertMessages from "common/Alert/AlertMessages";
import { useTranslation } from "react-i18next";
import { checkPrivileges } from "Helpers/CheckPrivileges";
import Loader from "layout/Loader";
import BackendPaginationTable from "common/Table/TableWithPagination";
import ComponentHeaderTwo from "common/ComponentHeaderTwo/ComponentHeaderTwo";
import { ReactComponent as Business } from "../../assets/SideBarIcons/business.svg";
import { useNavigate } from "react-router-dom";
import { Grid, Typography } from "@mui/material";
import StyledInput from "common/SearchField/Search";
import { useGetBusinessService } from "subModule/src/services/business/useBusiness";
import {
  BusinessResponse,
  ChangeStatusBusinessResponse,
  DeleteBusinessResponse,
} from "subModule/src/services/business/interface";
import FormDialog from "common/Modal/Modal";
import { BusinessProfileService } from "subModule/src/core/services";
import { selectUserInfo } from "features/user/userSlice";
import { useSelector } from "react-redux";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";

const PLATE_REGION_LABELS: Record<string, string> = {
  uae: "unitedArabEmirates",
  saudia: "saudiArabia",
  egypt: "egypt",
  custom: "custom",
};

const formatPlateFormat = (
  vehiclePlateFormat:
    | {
        region?: string;
        formats?: {
          title?: string;
          type?: string;
          min?: number;
          max?: number;
        }[];
      }
    | undefined,
  t: (key: string) => string,
) => {
  if (!vehiclePlateFormat) return "—";
  const regionKey = vehiclePlateFormat.region
    ? PLATE_REGION_LABELS[vehiclePlateFormat.region] ||
      vehiclePlateFormat.region
    : "";
  const regionLabel = regionKey ? t(regionKey) : "";
  const parts = (vehiclePlateFormat.formats || [])
    .map((f) => {
      if (!f?.title) return "";
      const range =
        f.min != null && f.max != null ? ` (${f.min}-${f.max})` : "";
      return `${f.title}${range}`;
    })
    .filter(Boolean);
  if (!regionLabel && !parts.length) return "—";
  if (regionLabel && parts.length) return `${regionLabel}`;
  return regionLabel;
};

export default function BusinessListing() {
  const navigate = useNavigate();
  const [pageNo, setPageNo] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(20);
  const [loading, setLoading] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<any>("");
  const [search, setSearch] = useState("");
  const [row, setRow] = useState<BusinessResponse>();
  const [statusModal, setStatusModal] = useState(false);
  const [archiveModal, setArchiveModal] = useState(false);
  const loggedInUserData = useSelector(selectUserInfo);
  const isOwner =
    loggedInUserData?.type === 100 || loggedInUserData?.type === -1;
  const canArchiveBusiness =
    loggedInUserData?.type === -1 && checkPrivileges("ROLE_BUSINESS_ARCHIVE");
  const {
    data: tableData,
    isFetching,
    refetch,
  } = searchTerm
    ? useGetBusinessService({
        pageNumber: pageNo,
        size: pageSize,
        title: searchTerm,
      })
    : useGetBusinessService({ pageNumber: pageNo, size: pageSize });
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();

  const listingRows = useMemo(() => {
    const content = tableData?.pages[0]?.data?.content || [];
    return content.map((item: BusinessResponse) => {
      const primary = item.primaryLanguage || "";
      const secondary = item.secondaryLanguage || "";
      const languages =
        primary && secondary
          ? `${primary} / ${secondary}`
          : primary || secondary || "—";
      return {
        ...item,
        whiteLabeled: item.whiteLabel ? t("yes") : t("no"),
        languages,
        currency: item.currency?.toUpperCase() || "—",
        timeZone: item.zone || "—",
        plateFormat: formatPlateFormat(item.vehiclePlateFormat, t),
      };
    });
  }, [tableData, t]);

  const fetchData = useCallback(() => {
    refetch().then((res: any) => {
      if (!res?.data?.pages[0]?.success)
        enqueueSnackbar(
          <AlertMessages
            text={res?.data?.pages[0]?.message || "Something went wrong"}
          />,
          { variant: "error" },
        );
    });
  }, []);
  useEffect(() => {
    fetchData();
  }, [pageNo, pageSize, searchTerm]);
  const handleChangePage = (pageNo: number) => {
    setPageNo(pageNo + 1);
  };
  const handleChangeRows = (pageSize: number) => {
    setPageSize(pageSize);
    setPageNo(1);
  };
  const showRowView = (row: any) => {
    if (loggedInUserData.type === -1 || loggedInUserData.type === 100)
      navigate("/business/" + row.id);
  };
  const clearSearch = () => {
    setSearch("");
    setSearchTerm("");
    if (searchTerm) {
      handleSearch("");
    }
  };
  const handleSearch = (search: string) => {
    setSearchTerm(search);
    setPageNo(1);
    setPageSize(pageSize);
  };
  const handleSearchClick = () => {
    const trimmedSearch = search.trim();
    if (trimmedSearch !== "") {
      handleSearch(trimmedSearch);
    }
  };
  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value.trim());
    if (!event.target.value.trim()) handleSearch("");
  };
  const onChangeStatus = (row: any) => {
    setRow(row);
    setStatusModal(true);
  };
  const onArchive = (row: any) => {
    setRow(row);
    setArchiveModal(true);
  };
  const actionslist = [
    ...(isOwner
      ? [{ label: "edit", onClick: showRowView, icon: <EditIcon /> }]
      : []),
    ...(canArchiveBusiness
      ? [{ label: "delete", onClick: onArchive, icon: <DeleteIcon /> }]
      : []),
  ];

  const columns = [
    { id: "title", label: t("title"), numeric: false, width: "20%" },
    // {
    //   id: "whiteLabeled",
    //   label: t("whiteLabeled"),
    //   numeric: false,
    //   width: "8%",
    // },
    {
      id: "languages",
      label: t("languages") + " " + t("Primary/Secondary"),
      numeric: false,
      width: "16%",
    },
    {
      id: "currency",
      label: t("currency") || "currency",
      numeric: false,
      width: "8%",
    },
    {
      id: "timeZone",
      label: t("timeZone") || "timeZone",
      numeric: false,
      width: "14%",
    },
    {
      id: "plateFormat",
      label: t("plateFormat") || "plateFormat",
      numeric: false,
      width: "17%",
    },
    ...(checkPrivileges("ROLE_BUSINESS_ACTIVE")
      ? [
          {
            id: "active",
            label: t("status") || "status",
            numeric: false,
            width: "8%",
          },
        ]
      : []),
    ...(actionslist.length
      ? [
          {
            id: "actions",
            label: t("actions") || "actions",
            numeric: false,
            width: "5%",
          },
        ]
      : []),
  ];

  return (
    <Box>
      <ComponentHeaderTwo
        showBtn={true}
        showSaveBtn={false}
        onClick={() => navigate("/business/create")}
        heading={t("Businessses")}
        icon={<Business className="text-black w-[34px] h-[34px]" />}
        btnText={"create"}
        btnType={"create"}
        showCreateBtn={checkPrivileges("ROLE_BUSINESS_CREATE")}
      />
      <Box>
        {isFetching || loading ? (
          <Loader />
        ) : (
          <div id="mainListingTable">
            <BackendPaginationTable
              columns={columns}
              data={listingRows}
              pageNo={pageNo}
              pageSize={pageSize}
              tableData={tableData?.pages[0]?.data || {}}
              showCheckbox={false}
              showActions={actionslist.length > 0}
              enableSorting={true}
              showHeader={true}
              showSearch={true}
              showFilter={true}
              showCreateButton={true}
              handleChangePage={handleChangePage}
              handleChangeRows={handleChangeRows}
              onChangeStatus={onChangeStatus}
              rowClick={true}
              loadingSearch={false}
              showRowView={showRowView}
              actions={actionslist}
              headerComponent={
                <StyledInput
                  // fullWidth
                  placeholder={t("title")}
                  handleSearchClick={handleSearchClick}
                  handleChange={handleInputChange}
                  value={search}
                  clearSearch={clearSearch}
                />
              }
            />
          </div>
        )}
      </Box>
      {statusModal && (
        <FormDialog
          open={statusModal}
          onClose={() => setStatusModal(false)}
          title="Change Status"
          content={
            <Grid container>
              <Grid item xs={12}>
                <Typography>{t(`Are you sure?`)}</Typography>
              </Grid>
            </Grid>
          }
          saveFunction={() => {
            setStatusModal(false);
            setLoading(true);
            BusinessProfileService.changeStatusBusiness(
              (row as any).id,
              row?.active ? "inactive" : "active",
              {},
            )
              .then((response: ChangeStatusBusinessResponse) => {
                if (response.success) {
                  refetch();
                  enqueueSnackbar(
                    <AlertMessages
                      statusCode={response.status}
                      text={
                        t("status") +
                        " " +
                        t("changed") +
                        " " +
                        t("successfully")
                      }
                    />,
                    { variant: "success" },
                  );
                } else {
                  enqueueSnackbar(
                    <AlertMessages statusCode={response.status} />,
                    { variant: "error" },
                  );
                }
              })
              .catch((err: any) => {
                console.error("===", err);
                enqueueSnackbar(
                  <AlertMessages
                    text={err?.data?.message || err || "Something went wrong"}
                  />,
                  { variant: "error" },
                );
              })
              .finally(() => setLoading(false));
          }}
          submitButtonText="OK"
        />
      )}
      {archiveModal && (
        <FormDialog
          open={archiveModal}
          onClose={() => setArchiveModal(false)}
          title={t("delete") + " " + t("business")}
          content={
            <Grid container>
              <Grid item xs={12}>
                <Typography>{t("Are you sure you want to delete?")}</Typography>
              </Grid>
            </Grid>
          }
          saveFunction={() => {
            setArchiveModal(false);
            setLoading(true);
            BusinessProfileService.deleteBusiness((row as any).id)
              .then((response: DeleteBusinessResponse) => {
                if (response.success) {
                  if (
                    tableData?.pages[0]?.data?.content?.length === 1 &&
                    pageNo > 1
                  )
                    setPageNo(1);
                  else refetch();
                  enqueueSnackbar(
                    <AlertMessages
                      statusCode={response.status}
                      text={
                        t("business") +
                        " " +
                        t("deleted") +
                        " " +
                        t("successfully")
                      }
                    />,
                    { variant: "success" },
                  );
                } else {
                  enqueueSnackbar(
                    <AlertMessages
                      statusCode={response.status}
                      text={response.message}
                    />,
                    { variant: "error" },
                  );
                }
              })
              .catch((err: any) => {
                console.error("===", err);
                enqueueSnackbar(
                  <AlertMessages
                    text={
                      err?.data?.message ||
                      err?.message ||
                      "Something went wrong"
                    }
                  />,
                  { variant: "error" },
                );
              })
              .finally(() => setLoading(false));
          }}
          submitButtonText="OK"
        />
      )}
    </Box>
  );
}
